/* Group each bike with its own upgrades in the cart.
   ==========================================================================
   THE PROBLEM
   Shopify lists cart lines newest-first, and the builder adds the bike first
   and its upgrades after. So the cart came out upside down: four "Builder
   Add-ons" rows, then the Custom Mini Surron they belong to. With two builds
   in the cart it was worse - eight loose upgrade rows and no way to tell which
   bike each belonged to except by reading the "For build" line, which says the
   same thing on both when the two builds are the same model.

   THE SHAPE WE WANT
     Custom Mini Surron          <- bike
       Guts Seat Cover           <- its upgrades, indented under it
       EBMX Controller
     Custom Mini Surron          <- second build, its own group
       Motor Cover
     Mini Stark Varg             <- plain products keep their own place

   WHY JAVASCRIPT
   Every line of one build shares a `_build` property stamped by cart-builds.js.
   The underscore makes it private, so Liquid deliberately never renders it -
   there is nothing in the DOM to group on, and CSS cannot see it. The mapping
   has to come from /cart.js, which does return private properties.

   Lines are matched back to their rows through data-key, which cart-products
   .liquid already writes on every <tr> and which is stable across quantity
   changes.

   WHY `order` AND NOT moving nodes
   The theme morphs the cart markup in place on every update (morph.js) and
   re-parents rows as it goes. Anything we physically moved would be reverted,
   or worse, would fight the morph and duplicate a row. Setting `order` on a
   flex container is purely visual - the DOM stays exactly where the theme
   expects it, and the removal animation, the quantity controls and the section
   re-render all keep working untouched.

   FAILS SOFT. If /cart.js is unreachable, or a row's key isn't in the map yet,
   nothing is ordered and nothing is hidden - you get the stock cart, in stock
   order, with the "For build" line still visible on every upgrade. That last
   part is why the "For build" line is hidden in CSS via a class this script
   adds, rather than unconditionally: when the grouping doesn't run, the only
   thing tying an upgrade to its bike stays on screen.
   ========================================================================== */
(function cartOrder() {
  var ADDON = /builder-add-ons/i;
  var map = null; /* data-key -> { build, addon, index } */
  var inflight = null;
  var timer = null;

  /* ---- the key -> build map ---------------------------------------------- */

  function fetchMap() {
    if (inflight) return inflight;
    inflight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var next = {};
        (cart.items || []).forEach(function (item, i) {
          next[item.key] = {
            build: (item.properties && item.properties._build) || null,
            addon: ADDON.test(item.url || ''),
            index: i,
          };
        });
        map = next;
        inflight = null;
        return map;
      })
      .catch(function () {
        inflight = null;
        return null;
      });
    return inflight;
  }

  /* True when we already know every row on screen, so we can arrange straight
     away and skip the round trip. Keys survive quantity edits, so this is the
     common case after the first load. */
  function haveAllKeys(rows) {
    if (!map) return false;
    for (var i = 0; i < rows.length; i++) {
      var k = rows[i].getAttribute('data-key');
      if (!k || !map[k]) return false;
    }
    return true;
  }

  /* ---- arranging ---------------------------------------------------------- */

  function arrangeTable(table) {
    var body = table.querySelector('tbody');
    if (!body) return;

    var rows = [].slice.call(body.querySelectorAll('.cart-items__table-row'));
    if (!rows.length || !map) return;

    var groups = [];
    var byBuild = {};

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var info = map[row.getAttribute('data-key')];

      /* Unknown row - bail out entirely rather than render a half-grouped
         cart, which would be more confusing than the stock order. */
      if (!info) return;

      if (!info.build) {
        groups.push({ pos: info.index, loose: row });
        continue;
      }

      var g = byBuild[info.build];
      if (!g) {
        g = byBuild[info.build] = { pos: info.index, bike: null, addons: [] };
        groups.push(g);
      }
      if (info.index < g.pos) g.pos = info.index;
      if (info.addon) g.addons.push(row);
      else g.bike = row;
    }

    /* Group position follows the earliest line of that build, so the most
       recently added build stays at the top - same as the stock cart. */
    groups.sort(function (a, b) {
      return a.pos - b.pos;
    });

    var order = 0;
    var last = null;

    groups.forEach(function (g) {
      var seq = g.loose ? [g.loose] : [g.bike].concat(g.addons);
      var grouped = !g.loose && !!g.bike && g.addons.length > 0;

      seq.forEach(function (row, idx) {
        if (!row) return;
        row.style.order = ++order;
        row.classList.toggle('pm-cart-parent', grouped && idx === 0);
        row.classList.toggle('pm-cart-child', grouped && idx > 0);
        row.classList.remove('pm-cart-groupend', 'pm-cart-last');
        last = row;
      });

      /* Divider goes at the foot of the whole group, not between a bike and
         its own upgrades. */
      var tail = seq.filter(Boolean).pop();
      if (tail) tail.classList.add('pm-cart-groupend');
    });

    if (last) last.classList.add('pm-cart-last');

    /* `order` only means something inside a flex or grid container, and tbody
       is neither by default. Rows keep their own display:grid, so their
       internal media/details/quantity/price layout is untouched. */
    body.style.display = 'flex';
    body.style.flexDirection = 'column';
  }

  function arrange() {
    var tables = document.querySelectorAll('.cart-items__table');
    if (!tables.length) return;

    var rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    if (!rows.length) return;

    if (haveAllKeys(rows)) {
      for (var i = 0; i < tables.length; i++) arrangeTable(tables[i]);
      return;
    }

    fetchMap().then(function (m) {
      if (!m) return;
      var t = document.querySelectorAll('.cart-items__table');
      for (var j = 0; j < t.length; j++) arrangeTable(t[j]);
    });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(arrange, 20);
  }

  /* ---- when to run -------------------------------------------------------- */

  /* Start the map request immediately, so on the cart page it has usually
     landed by the time the rows are parsed and there is no visible reshuffle. */
  fetchMap();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  /* cart-refresh.js fires this after every add, quantity change and removal.
     Keys change when lines are created, so drop the map and refetch. */
  document.addEventListener('cart:update', function () {
    map = null;
    schedule();
  });

  /* The theme also re-renders the cart through the Section Rendering API on
     paths that don't go through cart:update (drawer open, back/forward cache).
     Watching for replaced tables covers those without needing to know which. */
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (
            n.classList.contains('cart-items__table') ||
            n.querySelector('.cart-items__table')
          ) {
            schedule();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.__pmCartOrder = { arrange: arrange, refresh: function () { map = null; schedule(); } };
})();